import { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { api, formatCurrency } from '../lib/api';

const BulkPaymentsModal = ({ onClose, onSaved }) => {
  const [invoices, setInvoices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [rows, setRows] = useState([
    { invoice_id: '', amount: '', date: new Date().toISOString().slice(0, 10), bank_account_id: '', mode: 'NEFT', reference: '', notes: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/invoices', { params: { status: 'pending' } }).then(r => setInvoices(r.data));
    api.get('/bank-accounts').then(r => setBanks(r.data));
  }, []);

  const addRow = () => setRows([...rows, { invoice_id: '', amount: '', date: new Date().toISOString().slice(0, 10), bank_account_id: '', mode: 'NEFT', reference: '', notes: '' }]);
  const remRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const setRow = (idx, field, val) => setRows(rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const payments = rows
        .filter(r => r.invoice_id && r.amount && r.bank_account_id)
        .map(r => ({ ...r, amount: parseFloat(r.amount) }));
      if (payments.length === 0) throw new Error('Add at least one complete row.');
      const res = await api.post('/payments/bulk', { payments });
      onSaved && onSaved(res.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-transparent border border-border focus:border-maroon outline-none px-2 py-1.5 font-body text-xs w-full";

  return (
    <div data-testid="bulk-payments-modal" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-cream border border-border w-[min(1100px,98vw)] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-0.5">— Bulk Entry</p>
            <h2 className="font-heading text-lg font-medium">Add Multiple Payments</h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-maroon"><X className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-auto p-5 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Invoice</th>
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Amount</th>
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Date</th>
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Bank</th>
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Mode</th>
                  <th className="font-body text-[10px] uppercase tracking-[0.15em] text-text-secondary pb-2">Reference / UTR</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} data-testid={`bulk-row-${idx}`} className="border-t border-border">
                    <td className="py-2 pr-2 min-w-[180px]">
                      <select required value={r.invoice_id} onChange={(e) => setRow(idx, 'invoice_id', e.target.value)} className={inputCls}>
                        <option value="">Pick invoice...</option>
                        {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_no} — {inv.client_name}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2 w-32"><input required type="number" step="0.01" placeholder="0.00" value={r.amount} onChange={(e) => setRow(idx, 'amount', e.target.value)} className={inputCls} /></td>
                    <td className="py-2 pr-2 w-36"><input required type="date" value={r.date} onChange={(e) => setRow(idx, 'date', e.target.value)} className={inputCls} /></td>
                    <td className="py-2 pr-2 min-w-[140px]">
                      <select required value={r.bank_account_id} onChange={(e) => setRow(idx, 'bank_account_id', e.target.value)} className={inputCls}>
                        <option value="">Pick bank...</option>
                        {banks.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2 w-28">
                      <select value={r.mode} onChange={(e) => setRow(idx, 'mode', e.target.value)} className={inputCls}>
                        <option>NEFT</option><option>UPI</option><option>IMPS</option><option>RTGS</option><option>Cash</option><option>Cheque</option>
                      </select>
                    </td>
                    <td className="py-2 pr-2"><input type="text" value={r.reference} onChange={(e) => setRow(idx, 'reference', e.target.value)} className={inputCls} placeholder="UTR / cheque no." /></td>
                    <td className="py-2 w-8">
                      {rows.length > 1 && (
                        <button type="button" onClick={() => remRow(idx)} className="text-text-secondary hover:text-maroon" data-testid={`bulk-remove-row-${idx}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid="bulk-add-row" type="button" onClick={addRow} className="font-body text-sm text-maroon hover:underline flex items-center"><Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Add another row</button>

          {err && <p className="text-maroon font-body text-sm">{err}</p>}
        </form>
        <div className="flex items-center justify-between px-5 py-3 border-t border-border">
          <div className="font-body text-sm text-text-secondary">
            Total: <span className="font-numbers text-text-primary">{formatCurrency(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="font-heading text-sm text-text-secondary hover:text-maroon px-4 py-2">Cancel</button>
            <button data-testid="bulk-submit" type="button" disabled={busy} onClick={submit} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm transition-colors disabled:opacity-50">{busy ? 'Saving…' : `Save ${rows.length} Payment(s)`}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPaymentsModal;
